import React, { FC, useEffect, useCallback, useMemo } from 'react';

import { Container, NavBreadCrumbs } from 'pages/CourseList/style';
import { useNavigate, Navigate, useParams, Link } from 'react-router-dom';
import InfiniteScroll from 'react-infinite-scroll-component';
import { get, map, size } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Grid, Stack } from '@mui/material';

import Loader from 'elements/Loader';
import { COURSE_CATEGORY } from 'constant';
import SectionHeader from 'components/SectionHeader';
import CourseCard from 'pages/Dashboard/components/CourseCard';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { getQueryParam } from 'utils/utility';

import {
  getSuggestedCourses,
  getCompletedEnrolledCourses,
  setCourseRegistration,
  updateSuggestedCourses,
} from 'pages/Dashboard/ducks/slice';
import {
  getSuggestedCoursesSelector,
  getCompletedCoursesSelector,
} from 'pages/Dashboard/ducks/selectors';

const renderBackButton = (
  <div className='back-btn'>
    <ArrowBackIosIcon />
    Back Home
  </div>
);
const CourseList: FC<any> = () => {
  const navigate = useNavigate();
  const { category } = useParams<any>();
  const dispatch = useDispatch<any>();

  const { isLoading: isSuggestedCoursesLoading, data: suggestedCourses } =
    useSelector(getSuggestedCoursesSelector);

  const { isLoading: isCompletedCoursesLoading, data: completedCourses } =
    useSelector(getCompletedCoursesSelector);

  const isSuggestedCourses = category === COURSE_CATEGORY.SUGGESTED;
  const isCompletedCourses = category === COURSE_CATEGORY.COMPLETED;
  const isValidCategory = isSuggestedCourses || isCompletedCourses;
  const isLoading = isCompletedCoursesLoading || isSuggestedCoursesLoading;
  const coursesData = useMemo(
    () =>
      !isValidCategory
        ? {}
        : isSuggestedCourses
        ? suggestedCourses
        : completedCourses,
    [completedCourses, isSuggestedCourses, isValidCategory, suggestedCourses],
  );

  const getNextData = useCallback(() => {
    const next = get(coursesData, 'next', null);
    const { page }: any = getQueryParam(next || '');
    const props = { page };

    if (!next) {
      return;
    }

    if (isSuggestedCourses) {
      dispatch(getSuggestedCourses(props));
      return;
    }

    dispatch(getCompletedEnrolledCourses(props));
  }, [coursesData, dispatch, isSuggestedCourses]);

  const handleActionButton = () => {
    navigate(``);
  };

  useEffect(() => {
    const props = { page: 1 };

    if (!isValidCategory) {
      return;
    }

    if (isSuggestedCourses) {
      dispatch(getSuggestedCourses(props));

      return;
    }

    dispatch(getCompletedEnrolledCourses(props));
  }, [dispatch, isSuggestedCourses, isValidCategory]);

  if (!isValidCategory) {
    return <Navigate to="" replace />;
  }

  const courseCategory = isCompletedCourses
    ? 'Completed'
    : 'Suggested Learning';

  const handleCardRegistration = async (id: any, enrollmentAction: any) => {
    try {
      const params = { page: 1, resetInitialState: true };
      await dispatch(setCourseRegistration({ id, enrollmentAction })).unwrap();

      dispatch(updateSuggestedCourses({ id } as any));
      dispatch(getSuggestedCourses(params));
    } catch (err) {}
  };


  return (
    <Container>
      <div role="navigation" aria-label="breadcrumbs">
        <NavBreadCrumbs>
          <li>
            <Link to="">Paths & Learning</Link>
          </li>
          <li>
            <span className='separator'>/</span>
          </li>
          <li>{courseCategory}</li>
        </NavBreadCrumbs>
      </div>

      <SectionHeader
        title={courseCategory}
        buttonText={renderBackButton}
        handleActionButton={handleActionButton}
      />

      {isLoading ? 
        <div className='loader'>
          <Loader />
        </div>
        :
        get(coursesData, 'results', []).length < 1 && !isLoading ?
          <Stack alignItems='center' justifyContent='center' pt={15}>
            There are no {courseCategory} learnings to show.
          </Stack>
        :
        <InfiniteScroll
          dataLength={get(coursesData, 'results', []).length}
          next={getNextData}
          hasMore={get(coursesData, 'next', '') ? true : false}
          loader={
            <div className='loader' key={0}>
              <Loader />
            </div>
          }
          scrollableTarget='scrollableDiv'
        >
          <Grid container spacing={3}>
            {map(get(coursesData, 'results'), (cardData, index) => (
              <Grid item xs={12} sm={4} md={3} key={index}>
                <CourseCard
                  data={cardData}
                  handleCardRegistration={handleCardRegistration}
                  isLoading={isSuggestedCoursesLoading}
                />
              </Grid>
            ))}
          </Grid>
        </InfiniteScroll>
      }

      
    </Container>
  );
};

export default CourseList;
